import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { Mail, CheckCircle2 } from "lucide-react";
import { useAuthStore } from "../../stores/auth";
import { AuthLayout } from "../../components/layout/AuthLayout";
import { Input } from "../../components/ui/Input";
import { Button } from "../../components/ui/Button";

export function ForgotPasswordPage() {
  const forgotPassword = useAuthStore((state) => state.forgotPassword);
  const error = useAuthStore((state) => state.error);
  const clearError = useAuthStore((state) => state.clearError);
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch {
      // Error rendered from the store.
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthLayout>
      <h1>Reset your password</h1>
      <p className="auth-card__subtitle">
        {sent
          ? "Check your inbox for the reset link."
          : "Enter your email and we'll send you a link to reset your password."}
      </p>

      {sent ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "var(--z-space-4)",
            padding: "var(--z-space-8) var(--z-space-4)",
            background: "var(--z-success-50)",
            borderRadius: "var(--z-radius-lg)",
            textAlign: "center",
          }}
          role="status"
        >
          <CheckCircle2 size={40} style={{ color: "var(--z-success-500)" }} />
          <p style={{ color: "var(--z-gray-700)", fontSize: "0.9375rem" }}>
            If an account exists for <strong>{email}</strong>, we've sent a
            password reset link. The link expires in one hour.
          </p>
        </div>
      ) : (
        <form className="auth-form" onSubmit={onSubmit}>
          <Input
            label="Email address"
            type="email"
            name="email"
            autoComplete="email"
            required
            placeholder="you@example.com"
            icon={<Mail size={16} />}
            value={email}
            onChange={(event) => {
              clearError();
              setEmail(event.target.value);
            }}
          />

          {error ? (
            <div className="form-error" role="alert">
              {error}
            </div>
          ) : null}

          <Button type="submit" loading={pending} fullWidth size="lg">
            Send reset link
          </Button>
        </form>
      )}

      <div className="auth-card__footer">
        <p>
          <Link to="/login">← Back to sign in</Link>
        </p>
      </div>
    </AuthLayout>
  );
}
